import { useEffect, useState } from "react";
import {
  getVisitorDetail,
  getVisitorIdDocumentObjectUrl,
  getVisitorPhotoObjectUrl,
  getVisitorVisits,
} from "./api";
import type { VisitorDetail, VisitorVisit } from "./types";
import { formatDateTime } from "../dashboard/format";

type Props = {
  visitorId: number;
  onClose: () => void;
};

export function VisitorDetailModal({ visitorId, onClose }: Props) {
  const [detail, setDetail] = useState<VisitorDetail | null>(null);
  const [visits, setVisits] = useState<VisitorVisit[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [idDocUrl, setIdDocUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Escape closes.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    // Object URLs created for the photo/ID; revoked on cleanup to avoid leaks.
    let createdPhotoUrl: string | null = null;
    let createdIdUrl: string | null = null;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [record, visitList] = await Promise.all([
          getVisitorDetail(visitorId),
          getVisitorVisits(visitorId),
        ]);
        if (cancelled) return;
        setDetail(record);
        setVisits(visitList);

        if (record.has_photo) {
          try {
            createdPhotoUrl = await getVisitorPhotoObjectUrl(visitorId);
            if (!cancelled) setPhotoUrl(createdPhotoUrl);
          } catch {
            /* photo optional -- ignore load failure */
          }
        }
        if (record.has_id_document) {
          try {
            createdIdUrl = await getVisitorIdDocumentObjectUrl(visitorId);
            if (!cancelled) setIdDocUrl(createdIdUrl);
          } catch {
            /* id doc optional */
          }
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : "Failed to load visitor");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
      if (createdPhotoUrl) URL.revokeObjectURL(createdPhotoUrl);
      if (createdIdUrl) URL.revokeObjectURL(createdIdUrl);
    };
  }, [visitorId]);

  return (
    <div
      className="detail-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Visitor details"
      onClick={onClose}
    >
      <div className="detail-modal" onClick={(event) => event.stopPropagation()}>
        <header className="detail-header">
          <h3>{detail?.full_name ?? "Visitor"}</h3>
          <button type="button" className="secondary-btn" onClick={onClose}>
            Close
          </button>
        </header>

        {loading ? <p className="management-note">Loading…</p> : null}
        {error ? <p className="error-banner">{error}</p> : null}

        {detail && !loading ? (
          <div className="detail-body">
            <div className="detail-media">
              <div className="detail-media-block">
                <span className="detail-label">Photo</span>
                {photoUrl ? (
                  <img className="detail-photo" src={photoUrl} alt="Visitor" />
                ) : (
                  <div className="detail-empty">{detail.has_photo ? "—" : "Not captured"}</div>
                )}
              </div>
              <div className="detail-media-block">
                <span className="detail-label">ID document</span>
                {idDocUrl ? (
                  <a href={idDocUrl} target="_blank" rel="noreferrer">
                    <img className="detail-photo" src={idDocUrl} alt="ID document" />
                  </a>
                ) : (
                  <div className="detail-empty">{detail.has_id_document ? "—" : "Not captured"}</div>
                )}
              </div>
            </div>

            <dl className="detail-facts">
              <div>
                <dt>Phone</dt>
                <dd>{detail.phone || "—"}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{detail.email || "—"}</dd>
              </div>
              <div>
                <dt>Company</dt>
                <dd>{detail.company || "—"}</dd>
              </div>
              <div>
                <dt>ID type</dt>
                <dd>{detail.id_type || "—"}</dd>
              </div>
              <div>
                <dt>ID number</dt>
                <dd>{detail.has_id_number ? "On file (stored securely)" : "—"}</dd>
              </div>
              <div>
                <dt>Blacklisted</dt>
                <dd>{detail.is_blacklisted ? "Yes" : "No"}</dd>
              </div>
              <div>
                <dt>Registered</dt>
                <dd>{formatDateTime(detail.created_at ?? undefined)}</dd>
              </div>
            </dl>

            <h4 className="detail-subhead">Visit history ({visits.length})</h4>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Host</th>
                    <th>Site</th>
                    <th>Status</th>
                    <th>ID</th>
                    <th>Laptop</th>
                    <th>Checked in</th>
                    <th>Checked out</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty-cell">
                        No visits recorded.
                      </td>
                    </tr>
                  ) : (
                    visits.map((v) => (
                      <tr key={v.visit_id}>
                        <td>{v.purpose}</td>
                        <td>{v.host_name || "—"}</td>
                        <td>{v.site_name || "—"}</td>
                        <td>{v.status}</td>
                        <td>
                          {v.gate_reverify_required
                            ? "⚠ Re-verify"
                            : v.id_verified === true
                              ? "✓ Verified"
                              : v.id_verified === false
                                ? "Unverified"
                                : "—"}
                        </td>
                        <td>
                          {v.has_laptop
                            ? [v.laptop_mac_address, v.laptop_serial_number].filter(Boolean).join(" / ") ||
                              "Yes"
                            : "—"}
                        </td>
                        <td>{formatDateTime(v.checkin_time ?? undefined)}</td>
                        <td>{formatDateTime(v.checkout_time ?? undefined)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
